import { useState, useEffect, useMemo, JSXElementConstructor, Key, ReactElement } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDAvatar from "components/MDAvatar";

import Sidenav from "parts/Sidenav";
import Configurator from "parts/Configurator";

import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import routes from "./routes";

import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

import { NotificationProvider } from "context/NotificationContext";
import { MessageNotificationProvider } from "context/MessageNotificationContext";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import profilePicture from "assets/images/default-profile.png";

import SignIn from "pages/auth/SignIn";
import SignUp from "pages/auth/SignUp";
import UpdatePassword from "pages/auth/UpdatePassword";
import AuthCallback from "pages/auth/AuthCallback";

import AccountSettings from "pages/account/AccountSettings";

import DeadlineBanner from "components/DeadlineBanner";

import { useMessageNotification } from "context/MessageNotificationContext";

import MessageCenter from "pages/MessageNotification/MessageCenter";

import { setDeadlineBannerManuallyHidden } from "context";

import GlobalLoader from "components/loading/GlobalLoader";

import { useSessionContext } from "context/SessionContext";
import baseRoutes from "./routes";

import { supabase } from "lib/supabaseClient";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    showDeadlineBanner,
    deadlineBannerManuallyHidden,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isDailyCoachPage = `${pathname}${typeof window === "undefined" ? "" : window.location.hash}`
    .toLowerCase()
    .includes("/pages/task/dailycoach");

  const shouldShowDeadlineBanner =
    !pathname.startsWith("/pages/auth/Sign-In") &&
    !pathname.startsWith("/pages/auth/Sign-up") &&
    !pathname.startsWith("/update-password") &&
    !pathname.startsWith("/auth/callback") &&
    !pathname.startsWith("/pages/account/AccountSettings") &&
    !pathname.startsWith("/pages/account/Payments") &&
    !pathname.startsWith("/pages/account/Invoice") &&
    !pathname.startsWith("/pages/ai-coaching/GoalSetting") &&
    !pathname.startsWith("/pages/ai-coaching/AIPlan") &&
    !pathname.startsWith("/pages/ai-coaching/AiReport") &&
    !pathname.startsWith("/pages/ai-coaching/FutureBoard") &&
    !pathname.startsWith("/pages/timer/PomodoroTimer") &&
    !pathname.startsWith("/pages/task/TaskManager") &&
    !isDailyCoachPage &&
    !pathname.startsWith("/pages/growth-rate/Story") &&
    !pathname.startsWith("/pages/growth-rate/Partner") &&
    !pathname.startsWith("/pages/growth-rate/Reward") &&
    !pathname.startsWith("/pages/growth-rate/PersonalStatusRecord") &&
    !pathname.startsWith("/pages/ai-talk/AiTalk") &&
    !pathname.startsWith("/pages/MessageNotification/MessageCenter") &&
    !pathname.startsWith("/pages/admin/Sales") &&
    !pathname.startsWith("/pages/admin/Admin");

  const { session, userInfo } = useSessionContext();
  const nickname = userInfo?.nickname || "ニックネーム未設定";
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const resolvedRoutes = useMemo(() => {
    return baseRoutes.map((route) => {
      if (route.key === "user-profile") {
        const avatarSrc = userInfo?.profile_image_url || profilePicture;
        
        return { 
          ...route, 
          name: nickname,
          icon: <MDAvatar src={avatarSrc} alt="user" size="sm" /> 
        };
      }
      return route;
    });
  }, [nickname, userInfo?.profile_image_url]);

  // Cache for the rtl
  useMemo(() => {
    const pluginRtl: any = rtlPlugin;
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [pluginRtl],
    });

    setRtlCache(cacheRtl);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "PASSWORD_RECOVERY") {
          console.log("Supabase: パスワードリカバリーイベントを検知しました");
          // 検知したら強制的にパスワード更新画面へ遷移
          navigate("/update-password", { replace: true });
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);
  // -------------------------------------------------------------

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  const { addMessage } = useMessageNotification();

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes: any[]): any =>
    allRoutes.map(
      (route: {
        collapse: any;
        route: string;
        component: ReactElement<any, string | JSXElementConstructor<any>>;
        key: Key;
      }) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          return <Route path={route.route} element={route.component} key={route.key} />;
        }

        return null;
      }
    );

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const renderDeadlineBanner = () =>
    shouldShowDeadlineBanner && showDeadlineBanner && !deadlineBannerManuallyHidden  &&
    !isMobile ?
    (
      <DeadlineBanner
        visible={true}
        onClose={() => setDeadlineBannerManuallyHidden(dispatch, true)}
      />
    ) : null;

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <NotificationProvider>
          <MessageNotificationProvider>
            {renderDeadlineBanner()}
            <CssBaseline />
            <GlobalLoader />
            {/* 中継ページではサイドナビを表示しない */}
            {layout === "dashboard" && pathname !== "/auth/callback" && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                  brandName="Chrono Spark"
                  routes={resolvedRoutes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <Configurator isMobile={isMobile} />
                {configsButton}
              </>
            )}
            {layout === "vr" && <Configurator isMobile={isMobile} />}
            <Routes>
              <Route path="/pages/auth/Sign-In" element={<SignIn />} />
              <Route path="/pages/auth/Sign-up" element={<SignUp />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/pages/account/AccountSettings" element={<AccountSettings />} />
              <Route path="/pages/MessageNotification/MessageCenter" element={<MessageCenter />} />
              {getRoutes(routes)}
              <Route path="/update-password" element={<UpdatePassword />} />

              <Route
                path="*"
                element={
                  session ? <Navigate to="/dashboard" /> : <Navigate to="/pages/auth/Sign-In" />
                }
              />
            </Routes>
          </MessageNotificationProvider>
        </NotificationProvider>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <NotificationProvider>
        <MessageNotificationProvider>
          {renderDeadlineBanner()}
          <CssBaseline />
          <GlobalLoader />
          {/* 中継ページではサイドナビを表示しない */}
          {layout === "dashboard" && pathname !== "/auth/callback" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName="Chrono Spark"
                routes={resolvedRoutes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator isMobile={isMobile} />
              {configsButton}
            </>
          )}
          {layout === "vr" && <Configurator isMobile={isMobile} />}
          <Routes>
            <Route path="/pages/auth/Sign-In" element={<SignIn />} />
            <Route path="/pages/auth/Sign-up" element={<SignUp />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/pages/account/AccountSettings" element={<AccountSettings />} />
            <Route path="/pages/MessageNotification/MessageCenter" element={<MessageCenter />} />
            {getRoutes(routes)}
            <Route path="/update-password" element={<UpdatePassword />} />

            <Route
              path="*"
              element={
                session ? <Navigate to="/dashboard" /> : <Navigate to="/pages/auth/Sign-In" />
              }
            />
          </Routes>
        </MessageNotificationProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}
